<template>
  <LayoutPage :title="['Management Summary', 'Frequency Of Visits']">
    <v-row>
      <v-col cols="12" md="8">
        <RegionAndFacilitySelect />
      </v-col>
      <v-col cols="12" md="4">
        <v-row>
          <v-col>
            <v-select
              v-model="selectedVisitsFilter"
              :items="numberOfVisitsOptions"
              label="Frequency # of Visits"
              item-text="name"
              item-id="value"
              outlined
              dense
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <div class="d-flex justify-end align-center">
      <DateTimeFilter
        v-model="dateTime"
        :ranges="['1m', '2m', '3m']"
        :initial-custom-range="initialCustomRange"
        default="1m"
        custom
        class="black-border"
        @range-selected="updateUrlParams"
      />
      <div class="ml-4">
        <SearchField v-model="tableState.search" />
      </div>
    </div>
    <v-skeleton-loader
      :loading="loading"
      type="table-thead, table-row-divider@10"
    >
      <v-data-table
        :items="tableData.items"
        :headers="tableData.headers"
        :search="tableState.search"
        :sort-by.sync="tableState.sortBy"
        :sort-desc.sync="tableState.sortDesc"
        :page.sync="tableState.page"
        :items-per-page.sync="tableState.itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      >
        <template #item.visitsNumber="{ item }">
          <TableCellWrapper
            :is-highlighted="highlightColumn === 'visitsNumber'"
          >
            {{ item.visitsNumber }}
          </TableCellWrapper>
        </template>
        <template #item.avgVisitDurationMinute="{ item }">
          <TableCellWrapper
            :is-highlighted="highlightColumn === 'visitsDuration'"
          >
            {{ roundValue(item.avgVisitDurationMinute) }}
          </TableCellWrapper>
        </template>
        <template #footer.prepend>
          <ButtonExportCSV
            class="order-last"
            :table-data="tableData.items"
            :headers="tableData.headers"
            filename="Frequency_Visits_Report"
          />
          <ButtonPrint
            class="order-last"
            :before-print="showAllItems"
            :after-print="restoreItemsPerPage"
          />
        </template>
      </v-data-table>
    </v-skeleton-loader>
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import RegionAndFacilitySelect from "../RegionAndFacilitySelect.vue";
import SearchField from "@atoms/SearchField.vue";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import useDashboard from "../useDashboard";
import { FACILITY_VISITS_REPORT } from "./graphql";
import { formatToISODate, getMonth, utcDate } from "@tod-ui/helpers/datetime";
import useDataTable from "@components/common/useDataTable";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import { FREQUENCY_VISITS, VISIT_DURATION } from "@tod-ui/constants/dashboard";
import { useRoute } from "vue-router/composables";
import { computed, ref, watchEffect } from "vue";
import TableCellWrapper from "@layout/TableCellWrapper.vue";
import { roundValue } from "@tod-ui/helpers/math";
import useUser from "@components/authentication/useUser";

export default {
  name: "PageFacilityVisitsReport",
  components: {
    LayoutPage,
    RegionAndFacilitySelect,
    SearchField,
    DateTimeFilter,
    ButtonExportCSV,
    ButtonPrint,
    TableCellWrapper,
  },
  setup() {
    const { defaultOrganization } = useUser();
    const timezone = computed(
      () => defaultOrganization.value?.timeZone || "UTC"
    );

    const initialCustomRange = computed(() => {
      if (!route.query.customDate === "true") {
        return null;
      }
      return {
        start: formatToISODate(dateTime.value.after, timezone.value),
        end: formatToISODate(dateTime.value.before, timezone.value),
      };
    });

    const { selectedFacilities } = useDashboard();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["id desc"],
    });
    const selectedTimeFilter = ref({ min: 0, max: undefined });
    const selectedVisitsFilter = ref({ min: 0, max: undefined });

    const currentMonth = computed(() => {
      return getMonth(0, 0, defaultOrganization.value.timeZone);
    });
    const currentDate = computed(() => {
      const date = new Date().toISOString();
      return formatToISODate(date, defaultOrganization.value.timeZone);
    });
    const route = useRoute();

    const dateTime = ref({
      after: route.query.after || currentMonth.value,
      before: route.query.before || currentDate.value,
    });

    const updateUrlParams = (rangeType, customRange = null) => {
      const params = new URLSearchParams(route.query);

      if (["1m", "2m", "3m"].includes(rangeType)) {
        params.delete("customDate");
        params.delete("customRange");
        params.set("range", rangeType);
      } else if (rangeType === "custom" && customRange) {
        params.set("customDate", "true");
        params.set("range", "custom");
        const start = utcDate(customRange.start).setZone(timezone.value);
        const end = utcDate(customRange.end).setZone(timezone.value);
        params.set(
          "customRange",
          JSON.stringify({
            start: start.toISO(),
            end: end.toISO(),
          })
        );
      }

      window.history.replaceState({}, "", `${route.path}?${params.toString()}`);
    };

    const highlightColumn = computed(() => route.query.highlight_column);
    const selectOption = computed(() => route.query.select_option);

    const numberOfVisitsOptions = ref([
      {
        name: "Select All",
        value: { min: undefined, max: undefined },
      },
      {
        name: FREQUENCY_VISITS.GREATER_THAN_9,
        value: { min: 9, max: undefined },
      },
      { name: FREQUENCY_VISITS.BETWEEN_6_AND_8, value: { min: 6, max: 8 } },
      { name: FREQUENCY_VISITS.BETWEEN_3_AND_5, value: { min: 3, max: 5 } },
      { name: FREQUENCY_VISITS.BETWEEN_1_AND_2, value: { min: 1, max: 2 } },
      {
        name: FREQUENCY_VISITS.LESS_THAN_1,
        value: { min: undefined, max: 0 },
      },
    ]);

    const durationPerVisitsOptions = [
      {
        name: VISIT_DURATION.GREATER_THAN_20,
        value: { min: 21, max: undefined },
      },
      { name: VISIT_DURATION.BETWEEN_10_AND_20, value: { min: 10, max: 20 } },
      { name: VISIT_DURATION.BETWEEN_2_AND_10, value: { min: 2, max: 10 } },
      { name: VISIT_DURATION.LESS_THAN_2, value: { min: undefined, max: 1 } },
    ];

    watchEffect(() => {
      if (highlightColumn.value === "visitsNumber") {
        selectedVisitsFilter.value = numberOfVisitsOptions.value.find(
          (item) => item.name === selectOption.value
        )?.value || { min: 0, max: undefined };
      } else if (highlightColumn.value === "visitsDuration") {
        selectedTimeFilter.value = durationPerVisitsOptions.find(
          (item) => item.name === selectOption.value
        )?.value || { min: undefined, max: undefined };
      }
    });

    const formattedDate = computed(() => ({
      after: formatToISODate(
        dateTime.value.after,
        defaultOrganization.value.timeZone
      ),
      before: formatToISODate(
        dateTime.value.before,
        defaultOrganization.value.timeZone
      ),
    }));

    return {
      selectedFacilities,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      highlightColumn,
      selectOption,
      selectedTimeFilter,
      selectedVisitsFilter,
      dateTime,
      durationPerVisitsOptions,
      numberOfVisitsOptions,
      formattedDate,
      roundValue,
      route,
      initialCustomRange,
      updateUrlParams,
    };
  },
  data() {
    return {
      averageVisitsReport: [],
    };
  },
  computed: {
    loading() {
      return this.$apollo.loading;
    },
    tableData() {
      return {
        headers: [
          { text: "Facility Name", value: "facilityName" },
          { text: "Smart Unit", value: "organizationName" },
          { text: "Renter", value: "responderName" },
          { text: "No Of Visits", value: "visitsNumber" },
          {
            text: "Average Duration Per Visit (minutes)",
            value: "avgVisitDurationMinute",
          },
          {
            text: "Subscription Length (Month)",
            value: "subscriptionLengthMonth",
          },
        ],
        items: this.averageVisitsReport,
      };
    },
  },
  apollo: {
    averageVisitsReport: {
      query: FACILITY_VISITS_REPORT,
      variables() {
        return {
          facilityIds: this.selectedFacilities.map((f) => f.id),
          avgTimeFilter:
            this.selectedTimeFilter.min === 0 &&
            this.selectedTimeFilter.max === undefined
              ? null
              : this.selectedTimeFilter,
          numberOfVisitsFilter: this.selectedVisitsFilter,
          dateFilter: this.formattedDate,
        };
      },
      update: (data) => data.facilityVisitsReport,
      skip() {
        return this.selectedFacilities.length === 0 || !this.dateTime.after;
      },
    },
  },
};
</script>
